import { cn } from "@faire/prototype-playground/lib/utils";

import { getEntityByToken } from "../../../data/get_entity";

import type { FavoriteCollection } from "./FavoriteCollectionPicker";
import type { ProductTokensByCollection } from "./savedStore";

interface CollectionCardProps {
  collection: FavoriteCollection;
  productTokensByCollection: ProductTokensByCollection;
  onClick: () => void;
  /** Overrides the displayed collection name (e.g. "All saved products"). */
  label?: string;
  /**
   * Fill the parent's width (for a wrapping grid) instead of the fixed
   * 256px carousel width. The image grid keeps its 3:2 aspect either way.
   */
  fluid?: boolean;
}

export const CollectionCard = ({
  collection,
  productTokensByCollection,
  onClick,
  label,
  fluid = false,
}: CollectionCardProps) => {
  const tokens = productTokensByCollection[collection.id] ?? [];
  const productImages = tokens
    .map((productToken) => getEntityByToken(productToken, "product")?.imageUrl)
    .filter((imageUrl): imageUrl is string => Boolean(imageUrl));

  const imageAt = (index: number): string | undefined =>
    productImages[index] ?? collection.imageUrl;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex cursor-pointer flex-col gap-2 text-left transition-opacity hover:opacity-90",
        fluid ? "w-full" : "w-[256px] shrink-0"
      )}
    >
      {/* One 2x2 hero cell plus two stacked cells; only the outer corners
          round so the three images read as one unit. */}
      <div className="grid aspect-[3/2] w-full grid-cols-3 grid-rows-2 gap-[2px] overflow-hidden rounded">
        <CollectionThumbnailSlot
          imageUrl={imageAt(0)}
          className="col-span-2 row-span-2"
        />
        <CollectionThumbnailSlot imageUrl={imageAt(1)} />
        <CollectionThumbnailSlot imageUrl={imageAt(2)} />
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="type-fs-paragraph-medium text-fs-text-primary line-clamp-1">
          {label ?? collection.name}
        </span>
        <span className="type-fs-label text-fs-text-subdued">
          {tokens.length === 1 ? "1 product" : `${tokens.length} products`}
        </span>
      </div>
    </button>
  );
};

interface CollectionThumbnailSlotProps {
  imageUrl?: string;
  className?: string;
}

const CollectionThumbnailSlot = ({
  imageUrl,
  className,
}: CollectionThumbnailSlotProps) =>
  imageUrl ? (
    <div className={cn("relative min-h-0 overflow-hidden", className)}>
      <img src={imageUrl} alt="" className="size-full object-cover" />
      {/* 3% black overlay so edges of white product images stay visible */}
      <div
        className="absolute inset-0 bg-(--slate-color-grey-1000)/[0.03]"
        aria-hidden
      />
    </div>
  ) : (
    <div
      className={cn("bg-fs-surface-secondary min-h-0", className)}
      aria-hidden
    />
  );
